import React, { JSX, PropsWithChildren } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router, useNavigate } from 'react-router'
import App from './components/App'
import '../app.css'
import { AppState, Auth0ContextInterface, Auth0Provider, Auth0ProviderOptions, User } from '@auth0/auth0-react'
import { isOverlayPath, isShopifyConnectedPath } from 'lib/paths'
import { chooseAuth0Options } from 'lib/auth0Options'
const {
  VITE_AUTH0_CLIENT_ID: clientId,
  VITE_AUTH0_DOMAIN: domain,
  VITE_GQL_ENDPOINT: gqlEndpoint,
  VITE_AUTH0_AUDIENCE: audienceSetting,
  VITE_AUTH0_REFRESH_TOKENS: refreshTokensSetting
} = import.meta.env

const { audience, refreshTokens } = chooseAuth0Options({
  audienceSetting,
  gqlEndpoint,
  refreshTokensSetting
})
const refreshTokenProps: Pick<Auth0ProviderOptions, 'useRefreshTokens' | 'useRefreshTokensFallback'> = refreshTokens
  ? { useRefreshTokens: true, useRefreshTokensFallback: true }
  : {}

const Auth0ProviderWithRedirectCallback = ({
  children,
  context,
  ...props
}: PropsWithChildren<Omit<Auth0ProviderOptions, 'context'>> & {
  context?: React.Context<Auth0ContextInterface<User>>
}): JSX.Element => {
  const navigate = useNavigate()
  const onRedirectCallback = (appState?: AppState): void => {
    void navigate((appState?.returnTo) ?? globalThis.location.pathname)
  }
  return (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      context={context}
      {...props}
    >
      {children}
    </Auth0Provider>
  )
}

const rootDOM = createRoot(document.getElementById('root') as Element)

const isPublicEntry =
  isOverlayPath(globalThis.location.pathname) ||
  isShopifyConnectedPath(globalThis.location.pathname)

rootDOM.render(
  <Router>
    {isPublicEntry
      ? <App />
      : (
        <Auth0ProviderWithRedirectCallback
          domain={domain}
          clientId={clientId}
          {...refreshTokenProps}
          authorizationParams={{
            redirect_uri: globalThis.location.origin,
            audience
          }}
        >
          <App />
        </Auth0ProviderWithRedirectCallback>
        )}
  </Router>
)
